
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, Zap, Target, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function StatsDrivenHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-slate-900 text-white font-sans">
      <header className="container mx-auto p-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold">DataCore</h2>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="#" className="text-slate-300 hover:text-white">
            Platform
          </a>
          <a href="#" className="text-slate-300 hover:text-white">
            Use Cases
          </a>
          <a href="#" className="text-slate-300 hover:text-white">
            Pricing
          </a>
          <Button className="bg-indigo-500 hover:bg-indigo-600 rounded-full">
            Request a Demo
          </Button>
        </nav>
        <div className="md:hidden">
            <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X/> : <Menu/>}
            </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-slate-800/80 backdrop-blur-sm">
            <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg">
                 <a href="#" className="text-slate-300 hover:text-white">Platform</a>
                 <a href="#" className="text-slate-300 hover:text-white">Use Cases</a>
                 <a href="#" className="text-slate-300 hover:text-white">Pricing</a>
                 <Button className="bg-indigo-500 hover:bg-indigo-600 rounded-full w-full mt-2">
                    Request a Demo
                </Button>
            </nav>
        </div>
      )}


      <main>
        <section className="py-20 md:py-32 px-4">
          <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight tracking-tighter">
                Turn Data into <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
                  Actionable Insight.
                </span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl">
                DataCore is the unified analytics platform that helps you
                understand customer behavior, optimize funnels, and drive
                exponential growth.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-indigo-500 hover:bg-indigo-600 rounded-full"
                >
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-700 hover:bg-slate-800 hover:text-white rounded-full"
                >
                  Talk to Sales
                </Button>
              </div>
            </div>
            <div className="relative mt-8 md:mt-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-lg blur-2xl opacity-20"></div>
              <Image
                src="https://picsum.photos/seed/dataviz1/1200/800"
                alt="A complex, glowing 3D data visualization dashboard"
                width={1200}
                height={800}
                className="rounded-lg shadow-2xl relative z-10"
                data-ai-hint="data visualization"
              />
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <p className="text-sm uppercase tracking-widest text-indigo-400 font-semibold">
                Trusted by industry leaders
              </p>
              <div className="mt-6 flex flex-wrap justify-center items-center gap-8 md:gap-12 text-slate-500 text-2xl font-bold">
                <span>Nexus</span>
                <span className="opacity-70">Vertex</span>
                <span>Quantum</span>
                <span className="opacity-70">Apex</span>
                <span>Pinnacle</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 text-center">
              <div className="border border-slate-800 bg-slate-900/50 p-8 rounded-lg">
                <TrendingUp className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                <h3 className="text-5xl font-bold">+73%</h3>
                <p className="mt-2 text-slate-400">Increase in Conversion</p>
              </div>
              <div className="border border-slate-800 bg-slate-900/50 p-8 rounded-lg">
                <Zap className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                <h3 className="text-5xl font-bold">-50%</h3>
                <p className="mt-2 text-slate-400">Reduction in Time-to-Insight</p>
              </div>
              <div className="border border-slate-800 bg-slate-900/50 p-8 rounded-lg sm:col-span-2 lg:col-span-1">
                <Target className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                <h3 className="text-5xl font-bold">12x</h3>
                <p className="mt-2 text-slate-400">Return on Investment</p>
              </div>
            </div>
            <p className="text-center mt-8 text-sm text-slate-500">
              *Average results reported by customers in our 2023 impact study.
            </p>
          </div>
        </section>

        <section className="bg-slate-950 py-24 px-4">
          <div className="container mx-auto grid md:grid-cols-2 gap-16 items-center">
            <div className="max-w-md">
              <h2 className="text-4xl font-bold tracking-tighter">
                One Source of Truth.
              </h2>
              <p className="mt-4 text-slate-300">
                Break down data silos. Integrate all your sources into a
                single, powerful dashboard for a complete view of your business.
              </p>
              <ul className="mt-6 space-y-3 text-slate-200">
                <li className="flex items-center gap-3">
                  <CheckIcon /> Unlimited Data Sources
                </li>
                <li className="flex items-center gap-3">
                  <CheckIcon /> Real-time Analytics
                </li>
                <li className="flex items-center gap-3">
                  <CheckIcon /> Customizable Dashboards
                </li>
              </ul>
            </div>
            <div>
              <Image
                src="https://picsum.photos/seed/dataviz2/1000/800"
                alt="A diagram showing multiple data sources flowing into a central hub"
                width={1000}
                height={800}
                className="rounded-lg opacity-70"
                data-ai-hint="data flow"
              />
            </div>
          </div>
        </section>
      </main>
      <footer className="border-t border-slate-800 py-12 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold">
            Ready to unlock your data's potential?
          </h2>
          <p className="mt-2 text-slate-400">
            Start your free 14-day trial today. No credit card required.
          </p>
          <Button
            size="lg"
            className="mt-8 bg-indigo-500 hover:bg-indigo-600 rounded-full"
          >
            Sign Up & Get Started <ArrowRight className="ml-2" />
          </Button>
          <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500 gap-4">
            <p>&copy; {new Date().getFullYear()} DataCore Technologies Inc.</p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-white">
                Privacy
              </a>
              <a href="#" className="hover:text-white">
                Terms
              </a>
              <a href="#" className="hover:text-white">
                Status
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-cyan-400 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);
